import { useCallback } from 'react';

export const useQuizScoring = (difficulty) => {
  
  const calculateScore = useCallback((isCorrect, timeTaken, baseTime, questionDifficulty = null) => {
    // Use question-specific difficulty if provided, otherwise fall back to default difficulty
    const scoringDifficulty = questionDifficulty || difficulty;
    
    // Get scoring parameters from difficulty configuration
    const basePoints = scoringDifficulty?.base_points || 1000;
    const quickTimeBonusMultiplier = scoringDifficulty?.quick_time_bonus_multiplier || 2;
    const lateTimeBonusMultiplier = scoringDifficulty?.late_time_bonus_multiplier || 0.5;
    const wrongTimeBonusMultiplier = scoringDifficulty?.wrong_time_bonus_multiplier || -1;
    const wrongPenaltyTimeMultiplier = scoringDifficulty?.wrong_penalty_time_multiplier || 2;

    // Time thresholds
    const fastThreshold = baseTime * 0.5;      // Fast: ≤ 50% of base time
    const onTimeThreshold = baseTime;         // On-time: ≤ 100% of base time  
    const lateThreshold = baseTime * 1.5;     // Late: ≤ 150% of base time

    let score = 0;

    if (isCorrect) {
      if (timeTaken <= fastThreshold) {
        // Fast correct answer: basePoints + (basePoints * quickTimeBonusMultiplier)
        score = basePoints + (basePoints * quickTimeBonusMultiplier);
      } else if (timeTaken <= onTimeThreshold) {
        // On-time correct answer: basePoints + (basePoints * lateTimeBonusMultiplier)
        score = basePoints + (basePoints * lateTimeBonusMultiplier);
      } else if (timeTaken <= lateThreshold) {
        // Late but correct: basePoints * 0.5
        score = basePoints * 0.5;
      } else {
        // Too late: 0 points (treated as timeout)
        score = 0;
      }
    } else {
      if (timeTaken <= onTimeThreshold) {
        // Wrong answer within time: 0 points
        score = 0;
      } else if (timeTaken <= lateThreshold) {
        // Wrong answer late: basePoints * wrongTimeBonusMultiplier (negative)
        score = basePoints * wrongTimeBonusMultiplier;
      } else {
        // Wrong answer penalty: basePoints * wrongTimeBonusMultiplier * wrongPenaltyTimeMultiplier (negative, higher penalty)
        score = basePoints * wrongTimeBonusMultiplier * wrongPenaltyTimeMultiplier;
      }
    }

    return Math.round(score);
  }, [difficulty]);

  const getScoreExplanation = useCallback((isCorrect, timeTaken, baseTime) => {
    const fastThreshold = baseTime * 0.5;
    const onTimeThreshold = baseTime;
    const lateThreshold = baseTime * 1.5;

    if (isCorrect) {
      if (timeTaken <= fastThreshold) {
        return 'Quick & Correct! 🚀';
      } else if (timeTaken <= onTimeThreshold) {
        return 'Correct! ✅';
      } else if (timeTaken <= lateThreshold) {
        return 'Late but Correct! ⏰';
      } else {
        return 'Too Late! ⏰';
      }
    } else {
      if (timeTaken <= onTimeThreshold) {
        return 'Wrong Answer ❌';
      } else if (timeTaken <= lateThreshold) {
        return 'Wrong & Late! ❌⏰';
      } else {
        return 'Wrong & Penalty! ❌⚠️';
      }
    }
  }, []);

  const getTimeCategory = useCallback((timeTaken, baseTime) => {
    const fastThreshold = baseTime * 0.5;
    const onTimeThreshold = baseTime;
    const lateThreshold = baseTime * 1.5;

    if (timeTaken <= fastThreshold) return 'fast';
    if (timeTaken <= onTimeThreshold) return 'ontime';
    if (timeTaken <= lateThreshold) return 'late';
    return 'toolate';
  }, []);

  const formatScore = useCallback((score) => {
    if (score > 0) {
      return `+${score.toLocaleString()}`;
    } else if (score < 0) {
      return score.toLocaleString();
    }
    return '0';
  }, []);

  const getScoringRules = useCallback(() => {
    if (!difficulty) return null;

    const {
      base_points = 1000,
      base_time,
      quick_time_bonus_multiplier = 2,
      late_time_bonus_multiplier = 0.5,
      wrong_time_bonus_multiplier = -1,
      wrong_penalty_time_multiplier = 2
    } = difficulty;

    const fastThreshold = base_time * 0.5;
    const onTimeThreshold = base_time;
    const lateThreshold = base_time * 1.5;

    return {
      basePoints: base_points,
      baseTime: base_time,
      fastThreshold,
      onTimeThreshold,
      lateThreshold,
      scoring: {
        fastCorrect: base_points + (base_points * quick_time_bonus_multiplier),
        onTimeCorrect: base_points + (base_points * late_time_bonus_multiplier),
        lateCorrect: base_points * 0.5,
        tooLateCorrect: 0,
        wrongOnTime: 0,
        wrongLate: base_points * wrong_time_bonus_multiplier,
        wrongPenalty: base_points * wrong_time_bonus_multiplier * wrong_penalty_time_multiplier
      }
    };
  }, [difficulty]);

  return {
    calculateScore,
    getScoreExplanation,
    getTimeCategory,
    formatScore,
    getScoringRules
  };
};
